<script setup lang="ts">
  import { inject, useCssModule } from 'vue';

  import { RADIO_GROUP_INJECTION } from '../../RadioGroup/RadioGroup.keys';

  export interface RadioVariantProps {
    /**
     * Whether the input should be disabled
     */
    disabled?: boolean;

    /**
     * Whether the group should expand to the parent's width
     */
    fullWidth?: boolean;

    /**
     * A unique string to distinguish one Radio instance from another
     * and to link the label to the input; required for accessibility
     */
    id: string | number;

    /**
     * The description which appears to the right of the radio button
     */
    label?: string;

    /**
     * value bound to the parent component
     */
    modelValue?: string | undefined;

    /**
     * Passed to the "name" attribute of the `<input>` element.
     */
    name?: string;

    /**
     * The value for the radio input.
     */
    value: string;
  }

  const props = defineProps<RadioVariantProps>();
  const classes = useCssModule();
  const radioGroupInjection = inject(RADIO_GROUP_INJECTION.key);

  const emit = defineEmits<{
    (e: 'input', event: Event): void;
  }>();
</script>

<template>
  <div
    data-variant-button
    :class="[
      classes.root,
      {
        'w-full': props.fullWidth,
        [classes['radio-group-vertical-orientation']]: radioGroupInjection?.orientation?.value === 'vertical',
      },
    ]"
  >
    <input
      :id="`${props.name}-${props.id}`"
      class="sr-only"
      :class="classes.input"
      type="radio"
      :name="props.name"
      :value="props.value"
      :checked="props.modelValue === props.value"
      :disabled="props.disabled || props.disabled"
      @input="emit('input', $event)"
    />
    <label :class="classes.label" :for="`${props.name}-${props.id}`">
      <slot>{{ props.label }}</slot>
    </label>
  </div>
</template>

<style module>
  .label {
    padding: 8px 30px;
    border: 1px solid var(--color-ice-500);
    font-weight: var(--stash-font-weight-semibold);
    color: var(--color-ice-700);
    cursor: pointer;
    transition: all 0.2s;
    user-select: none;
    display: block;
    text-align: center;
  }

  .root:first-of-type .label {
    border-top-left-radius: var(--radius-sm);
    border-bottom-left-radius: var(--radius-sm);
  }

  .root:last-of-type .label {
    border-top-right-radius: var(--radius-sm);
    border-bottom-right-radius: var(--radius-sm);
  }

  .root:not(:first-of-type) .label {
    margin-left: -1px;
  }

  .root:not(:last-of-type) .label {
    border-right-color: transparent;
  }

  .root.radio-group-vertical-orientation .label {
    border-right-color: var(--color-ice-500);
  }

  .input:disabled ~ .label {
    background-color: var(--color-ice-100);
    color: var(--color-ice-500);
    cursor: auto;
  }

  .root.radio-group-vertical-orientation:first-of-type .label {
    border-top-left-radius: var(--radius-sm);
    border-top-right-radius: var(--radius-sm);
    border-bottom-left-radius: 0;
  }

  .root.radio-group-vertical-orientation:last-of-type .label {
    border-top-right-radius: 0;
    border-bottom-right-radius: var(--radius-sm);
    border-bottom-left-radius: var(--radius-sm);
  }

  .root.radio-group-vertical-orientation:not(:first-of-type) .label {
    margin-left: 0;
    margin-top: -1px;
  }

  .input:not(:checked, :disabled) ~ .label:hover {
    border-color: var(--color-blue-500);
    color: var(--color-blue-500);
    z-index: 1;
    position: relative;
  }

  .input:checked:not(:disabled) ~ .label {
    border-color: var(--color-blue-500);
    color: var(--color-blue-500);
    background-color: var(--color-blue-100);
    z-index: 1;
    position: relative;
  }
</style>
